import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook that tracks whether a scrollable element has been scrolled to the bottom.
 * Returns a callback ref to attach to the scrollable element and the boolean state.
 * Resets when `resetKey` changes (e.g., when dialog reopens).
 */
export function useScrollToBottom(resetKey: unknown) {
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const hasReachedRef = useRef(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const checkIfAtBottom = useCallback((el: HTMLElement) => {
    if (hasReachedRef.current) return; // Already reached — skip work
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 10) {
      hasReachedRef.current = true;
      setHasReachedBottom(true);
      observerRef.current?.disconnect(); // Stop observing once done
    }
  }, []);

  // Reset when key changes (modal reopens)
  useEffect(() => {
    hasReachedRef.current = false;
    setHasReachedBottom(false);
  }, [resetKey]);

  // Callback ref — fires when element mounts/unmounts
  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      elementRef.current = node;

      if (node && !hasReachedRef.current) {
        checkIfAtBottom(node);

        // Only observe if we haven't already reached bottom
        if (!hasReachedRef.current) {
          observerRef.current = new ResizeObserver(() => checkIfAtBottom(node));
          observerRef.current.observe(node);
        }
      }
    },
    [checkIfAtBottom]
  );

  const onScroll = useCallback(() => {
    if (elementRef.current) {
      checkIfAtBottom(elementRef.current);
    }
  }, [checkIfAtBottom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return { scrollRef, onScroll, hasReachedBottom };
}
